import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { ArrowRight } from "lucide-react";

const SITE = SITE_URL;

const faqSections = [
  {
    title: "Delivery & Drop-off",
    faqs: [
      {
        q: "Do you deliver strollers to Walt Disney World resorts?",
        a: "Yes. We deliver to every Walt Disney World resort hotel. Delivery and pickup require an in-person lobby meeting — we do not leave strollers or gear unattended or with bell services.",
      },
      {
        q: "Does Disney allow stroller delivery to bell services?",
        a: "No. Walt Disney World does not allow third-party vendors to leave rental equipment with bell services or concierge. Every delivery and pickup at a Disney resort requires an in-person meeting between our team and the guest.",
      },
      {
        q: "Where exactly do we meet for delivery?",
        a: "We meet you in the lobby of your resort, hotel or vacation home. We will coordinate the exact meeting point and time with you before your rental begins.",
      },
      {
        q: "What are your delivery windows?",
        a: "We offer 1-hour delivery windows from 7 am to 10 pm — pick any hour that fits your schedule. Delivery is always free. We do not offer deliveries after 10:00 pm.",
      },
      {
        q: "Is delivery free?",
        a: "Standard delivery is free for all rentals in our service area, which covers Walt Disney World, Universal Orlando, area hotels, Kissimmee, Celebration, Reunion, Bonnet Creek and vacation rentals throughout the greater Orlando area.",
      },
      {
        q: "Can you deliver to Airbnb or VRBO properties?",
        a: "Yes. We deliver to private vacation homes, Airbnb and VRBO properties throughout the Orlando area. We coordinate a meeting time directly with you.",
      },
      {
        q: "Do you deliver to Universal Orlando?",
        a: "Yes. We deliver to Universal resort hotels and properties in the Universal Orlando area. Contact us when booking to confirm the logistics for your specific property.",
      },
    ],
  },
  {
    title: "Strollers",
    faqs: [
      {
        q: "Which stroller is best for Disney World?",
        a: "The Baby Jogger City Mini GT2 Single is our most popular stroller for Disney World. It folds with one hand, handles all terrain, meets Disney's size requirements and supports children up to 65 lbs. For families who want a full nap recline, the UPPAbaby VISTA V2 is another excellent choice.",
      },
      {
        q: "Should I rent a single or double stroller?",
        a: "If you have one child who still uses a stroller, a single is generally easier to navigate. A double stroller works well for two young children, or for families with an older child who may need to rest mid-day. Our stroller quiz can help you decide.",
      },
      {
        q: "What stroller is best for an older child at Disney?",
        a: "The BOB Single Stroller supports children up to 75 lbs and is one of our most comfortable options for larger or older children. For two kids where one is older, the Baby Jogger City Mini GT2 Double seats two children up to 65 lbs each.",
      },
      {
        q: "Can I take the stroller on Disney buses?",
        a: "Yes. Strollers are allowed on Disney buses when folded. You must fold your stroller before boarding.",
      },
      {
        q: "Can I take the stroller on the Disney Skyliner?",
        a: "Yes. Strollers are allowed on the Skyliner when folded and stored. You must fold the stroller before boarding the gondola.",
      },
      {
        q: "Can I take the stroller on the Disney Monorail?",
        a: "Yes. Folded strollers are allowed on the Monorail.",
      },
      {
        q: "What is Disney World's stroller size rule?",
        a: "Walt Disney World requires strollers to be no larger than 31 inches wide and 52 inches long. Every stroller in our inventory meets this requirement. Note that Disney has its own rental strollers inside the parks, but they are umbrella-style and do not recline.",
      },
      {
        q: "Do the strollers include snack trays?",
        a: "Several of our strollers include cup holders and/or snack trays. Each product page lists exactly what accessories are included at no extra charge. You can also view the included accessories when selecting a stroller during booking.",
      },
    ],
  },
  {
    title: "Sanitization & Quality",
    faqs: [
      {
        q: "Are your strollers cleaned between rentals?",
        a: "Yes. Every stroller and piece of equipment is cleaned and sanitized between rentals using hospital-grade disinfectants. We also inspect each item for wear and damage before it is rented again.",
      },
    ],
  },
  {
    title: "Booking & Pricing",
    faqs: [
      {
        q: "How far in advance should I reserve?",
        a: "We recommend booking as soon as your trip is confirmed. Spring break, summer and major holiday weeks fill up quickly. Reservations can be made weeks or months in advance.",
      },
      {
        q: "What happens if my flight is delayed?",
        a: "Contact us as soon as you know about the delay. We will do our best to adjust your delivery window. We understand travel delays happen and will work with you to accommodate a new schedule.",
      },
      {
        q: "Do you offer multi-day discounts?",
        a: "Yes. Our pricing automatically tiers down for longer rentals. Days 1–3 are at the standard rate, days 4–7 are discounted, and day 8 and beyond are discounted further. The exact pricing for your dates is shown when you select your rental period.",
      },
    ],
  },
];

const allFaqsFlat = faqSections.flatMap((s) => s.faqs);

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: allFaqsFlat.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "FAQ", item: `${SITE}/faq` },
  ],
};

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5 cursor-pointer"
      onClick={() => setOpen(!open)}
      data-testid="faq-item"
    >
      <div className="flex justify-between items-center gap-3">
        <h3 className="font-semibold text-[#F6F1E4] text-base leading-snug">{q}</h3>
        <span className={`text-[#FFD166] text-xl leading-none shrink-0 transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </div>
      {open && <p className="mt-3 text-[#B9C8D5] text-sm leading-relaxed">{a}</p>}
    </div>
  );
}

export default function FAQPage() {
  return (
    <>
      <PageSEO
        title="FAQ — Stroller & Baby Gear Rental Questions | Enchanted Gear Rentals"
        description="Answers to common questions about stroller rentals, Disney World delivery, pricing, sanitization and booking at Enchanted Gear Rentals in Orlando."
        canonical={`${SITE}/faq`}
        structuredData={[breadcrumbSchema, faqSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>
        <section className="pt-28 pb-12 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-2xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">FAQ</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-4">Frequently Asked Questions</h1>
            <p className="text-[#B9C8D5]">Everything you need to know about renting strollers and baby gear for your Orlando vacation.</p>
          </div>
        </section>

        <div className="container max-w-3xl py-12 pb-20 space-y-12">
          {faqSections.map((section) => (
            <section key={section.title}>
              <h2 className="font-serif text-2xl text-[#FFD166] mb-5">{section.title}</h2>
              <div className="space-y-3">
                {section.faqs.map((f) => <FAQItem key={f.q} q={f.q} a={f.a} />)}
              </div>
            </section>
          ))}

          <section className="text-center pt-4">
            <p className="text-[#B9C8D5] mb-5">Have a question we did not answer?</p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/contact"><button className="btn-primary" data-testid="faq-contact-cta">Contact Us <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <Link to="/#trip-setup"><button className="btn-ghost">Reserve a Stroller</button></Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}
